import { Page, View, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { globalStyles } from '../../styles';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { getDescriptionKeyByRegion } from '@utils/regionMapping';
import { APP_HOST } from '@utils/apiHooks';

export interface PackagePageProps {
  packageItem: any;
  event: PublicEventDetailsResponse;
  footer: JSX.Element;
}

const getList = (rawString: string): string[] =>
  rawString
    .split('\n')
    .map(item => item.trim())
    .filter(item => item.startsWith('-'))
    .map(item => item.slice(1).trim());

const PackagePage = ({ packageItem, event, footer }: PackagePageProps) => {
  const { t } = useTranslation();

  const descriptionKey = useMemo(() => {
    return getDescriptionKeyByRegion(event?.region || '');
  }, [event?.region]);

  const beforeTheEventList = useMemo(() => {
    const desc = packageItem?.description?.[descriptionKey]?.before_the_event;
    return desc ? getList(desc) : [];
  }, [packageItem, descriptionKey]);

  const duringTheEventList = useMemo(() => {
    const desc = packageItem?.description?.[descriptionKey]?.during_the_event;
    return desc ? getList(desc) : [];
  }, [packageItem, descriptionKey]);

  const afterTheEventList = useMemo(() => {
    const desc = packageItem?.description?.[descriptionKey]?.after_the_event;
    return desc ? getList(desc) : [];
  }, [packageItem, descriptionKey]);

  return (
    <Page size={{ width: 1440, height: 800 }} style={styles.page}>
      <View style={styles.columnContainer}>
        <View style={styles.leftColumn}>
          <Text style={{ ...styles.title, marginBottom: 20 }}>
            {packageItem?.name ?? ''}
          </Text>

          <View
            style={{
              ...globalStyles.lineBar,
              borderColor: event?.design?.themeColor ?? 'black',
              marginTop: 0,
              marginLeft: 0,
              marginBottom: 20,
            }}
          />

          <View style={styles.columnContent}>
            <Text style={styles.columnTitle}>{t('before-the-event')}</Text>
            <View
              style={{
                ...globalStyles.lineBar,
                borderColor: 'white',
                marginTop: 0,
                marginLeft: 0,
                marginBottom: 10,
              }}
            />
            {beforeTheEventList.map((item, idx) => (
              <Text key={`before-${idx}`} style={styles.listItem}>
                • {item}
              </Text>
            ))}
          </View>
        </View>

        <View style={styles.middleColumn}>
          <View
            style={{
              ...styles.columnContent,
              backgroundColor: event?.design?.themeColor,
              color: 'white',
            }}
          >
            <Text style={{ ...styles.columnTitle, color: 'white' }}>
              {t('at-the-event')}
            </Text>
            <View
              style={{
                ...globalStyles.lineBar,
                borderColor: 'white',
                marginLeft: 0,
                marginBottom: 10,
              }}
            />
            {duringTheEventList.map((item, idx) => (
              <Text
                key={`during-${idx}`}
                style={{ ...styles.listItem, color: 'white' }}
              >
                • {item}
              </Text>
            ))}
          </View>
        </View>

        <View style={styles.rightColumn}>
          <View style={{ ...styles.columnContent, backgroundColor: 'white' }}>
            <Text style={styles.columnTitle}>{t('after-the-event')}</Text>
            <View
              style={{
                ...globalStyles.lineBar,
                borderColor: '#F5F5F5',
                marginTop: 0,
                marginLeft: 0,
                marginBottom: 10,
              }}
            />
            {afterTheEventList.map((item, idx) => (
              <Text key={`after-${idx}`} style={styles.listItem}>
                • {item}
              </Text>
            ))}
          </View>
          <View style={styles.buyNowWrapper}>
            <Text
              style={{ ...styles.priceText, color: event?.design?.themeColor }}
            >
              {new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
              }).format(packageItem?.value)}
            </Text>
            <Text style={styles.excludeVATText}>{t('exl-19-vat')}</Text>

            <Link
              src={`${APP_HOST}/events/${String(event?.slug ?? '')}${String(t('partnering-packages-url') ?? '')}/${String(packageItem?.slug ?? '')}`}
              style={{
                ...styles.ticketButton,
                backgroundColor: event?.design?.themeColor || 'black',
              }}
            >
              {t('buy-now')}
            </Link>
          </View>
          {footer}
        </View>
      </View>
    </Page>
  );
};

export default PackagePage;
